"use client";

import { motion, type Transition } from "motion/react";
import { useId } from "react";

import { cn } from "@/lib/utils";

import {
  LOGO_INNER_STROKE,
  LOGO_OUTER_STROKE,
  LOGO_PATHS,
  LOGO_VIEWBOX,
} from "./logo-paths";

// =============================================================================

export {
  LOGO_INNER_STROKE,
  LOGO_OUTER_STROKE,
  LOGO_PATHS,
  LOGO_VIEWBOX,
} from "./logo-paths";

const PATHS = LOGO_PATHS;
const OUTER_STROKE = LOGO_OUTER_STROKE;
const INNER_STROKE = LOGO_INNER_STROKE;

type LogoProps = {
  className?: string;
  /** Accessible name, omit when the logo is decorative */
  title?: string;
  /** Draw the strokes on mount */
  animated?: boolean;
  /** Delay of the draw animation, in seconds */
  delay?: number;
  /** Duration of each stroke, in seconds */
  duration?: number;
  /** Disable the fading ends */
  solid?: boolean;
  /** Multiply the stroke widths (thicker footer watermark…) */
  weight?: number;
};

export function Logo({
  className,
  title,
  animated = false,
  delay = 0,
  duration = 0.8,
  solid = false,
  weight = 1,
}: LogoProps) {
  const id = useId().replace(/:/g, "");
  const bowlGradient = `logo-bowl-${id}`;
  const innerGradient = `logo-inner-${id}`;

  const transition = (order: number): Transition => ({
    pathLength: {
      duration,
      delay: delay + order * duration * 0.55,
      ease: [0.65, 0, 0.35, 1],
    },
    opacity: { duration: 0.01, delay: delay + order * duration * 0.55 },
  });

  const draw = (order: number) =>
    animated
      ? {
          initial: { pathLength: 0, opacity: 0 },
          animate: { pathLength: 1, opacity: 1 },
          transition: transition(order),
        }
      : {};

  return (
    <svg
      viewBox={LOGO_VIEWBOX}
      fill="none"
      strokeLinecap="round"
      className={cn("h-auto", className)}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {!solid && (
        <defs>
          <linearGradient
            id={bowlGradient}
            x1="256.426"
            y1="211.165"
            x2="351.413"
            y2="293.456"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="currentColor" stopOpacity="0" />
            <stop offset="1" stopColor="currentColor" />
          </linearGradient>
          <linearGradient
            id={innerGradient}
            x1="297.59"
            y1="347.798"
            x2="217.085"
            y2="347.881"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="currentColor" stopOpacity="0" />
            <stop offset="1" stopColor="currentColor" />
          </linearGradient>
        </defs>
      )}
      <g stroke="currentColor" strokeWidth={OUTER_STROKE * weight}>
        <motion.path d={PATHS.stemAndBowl} {...draw(0)} />
        <motion.path
          d={PATHS.bowlTop}
          stroke={solid ? undefined : `url(#${bowlGradient})`}
          {...draw(1)}
        />
      </g>
      <g stroke="currentColor" strokeWidth={INNER_STROKE * weight}>
        <motion.path d={PATHS.innerTop} {...draw(2)} />
        <motion.path
          d={PATHS.innerBottom}
          stroke={solid ? undefined : `url(#${innerGradient})`}
          {...draw(3)}
        />
      </g>
    </svg>
  );
}

// =============================================================================
